import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { User as UserIcon, Building2, ShieldCheck, HardDrive, Globe, AlertTriangle, ImagePlus, Loader2, Check, KeyRound, Plug } from "lucide-react";
import { useAuth } from "../../features/auth/context/AuthContext";
import { Field, Select, Button, Modal } from "../../shared/ui";
import { readImageAsDataUrl } from "../../features/shared/imageUpload";
import {
  updateProfile,
  changePassword,
  updateWorkspace,
  deleteWorkspace,
  get2FAStatus,
  setup2FA,
  enable2FA,
  disable2FA,
  type TwoFactorSetup,
} from "../../features/settings/settingsApi";
import SettingsShell, { type SettingsTab } from "../../features/settings/components/SettingsShell";
import StickySaveBar from "../../features/settings/components/StickySaveBar";
import StorageSettings from "./StorageSettings";
import DomainsSettings from "./DomainsSettings";
import IntegrationsSettings from "./IntegrationsSettings";

const TZ = ["UTC", "America/New_York", "America/Chicago", "America/Los_Angeles", "Europe/London", "Europe/Paris", "Asia/Kolkata", "Asia/Singapore", "Australia/Sydney"];
const LANGS = [{ value: "en", label: "English" }, { value: "fr", label: "Français" }, { value: "es", label: "Español" }, { value: "de", label: "Deutsch" }];
const CATEGORIES = ["general", "rings", "necklaces", "earrings", "bracelets", "mens", "gemstones"];

const TABS: SettingsTab[] = [
  { id: "profile", label: "Profile", icon: UserIcon },
  { id: "workspace", label: "Workspace", icon: Building2 },
  { id: "security", label: "Security", icon: ShieldCheck },
  { id: "storage", label: "Storage", icon: HardDrive },
  { id: "domains", label: "Domains", icon: Globe },
  { id: "integrations", label: "Integrations", icon: Plug },
  { id: "danger", label: "Danger zone", icon: AlertTriangle },
];

export default function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "profile";
  const setTab = (id: string) => setParams(id === "profile" ? {} : { tab: id });

  return (
    <SettingsShell tabs={TABS} active={tab} onChange={setTab}>
      {tab === "profile" && <ProfileSection />}
      {tab === "workspace" && <WorkspaceSection />}
      {tab === "security" && <SecuritySection />}
      {tab === "storage" && <StorageSettings />}
      {tab === "domains" && <DomainsSettings />}
      {tab === "integrations" && <IntegrationsSettings />}
      {tab === "danger" && <DangerSection />}
    </SettingsShell>
  );
}

function Section({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <div className="surface-card p-6">
      <h2 className="text-[15px] font-semibold text-ink-900">{title}</h2>
      {desc && <p className="mt-0.5 text-[12.5px] text-ink-500">{desc}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}

// ─────────────────────────── Profile ────────────────────────────────────────
function ProfileSection() {
  const { user, updateUser } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const initial = { name: user?.name ?? "", avatar: user?.avatar, timezone: user?.timezone ?? "UTC", language: user?.language ?? "en" };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const pick = async (file?: File) => {
    if (!file) return;
    try { const avatar = await readImageAsDataUrl(file, 256); setForm((f) => ({ ...f, avatar })); } catch { /* ignore */ }
  };
  const save = async () => {
    setSaving(true);
    try {
      const u = await updateProfile(form);
      updateUser({ name: u.name, avatar: u.avatar, timezone: u.timezone, language: u.language });
    } finally { setSaving(false); }
  };

  return (
    <Section title="Profile" desc="Your personal details.">
      <div className="flex items-center gap-4">
        <button type="button" onClick={() => fileRef.current?.click()} className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-ink-200 bg-canvas text-ink-400 hover:border-gold-300">
          {form.avatar ? <img src={form.avatar} alt="" className="h-full w-full object-cover" /> : <ImagePlus size={20} />}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        <div>
          <p className="text-[13px] font-medium text-ink-800">Profile photo</p>
          <p className="text-[11.5px] text-ink-400">PNG or JPG, square works best.</p>
          {form.avatar && <button onClick={() => setForm((f) => ({ ...f, avatar: "" }))} className="mt-1 text-[11.5px] text-rose hover:underline">Remove</button>}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Full name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <Field label="Email" value={user?.email ?? ""} disabled />
        <Select label="Timezone" value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))} options={TZ.map((t) => ({ value: t, label: t }))} />
        <Select label="Language" value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))} options={LANGS} />
      </div>

      <StickySaveBar visible={dirty} saving={saving} onSave={save} onDiscard={() => setForm(initial)} />
    </Section>
  );
}

// ─────────────────────────── Workspace ──────────────────────────────────────
function WorkspaceSection() {
  const { activeWorkspace, hasPermission, refreshWorkspaces } = useAuth();
  const canManage = hasPermission("*") || hasPermission("mgmt.team.invite");
  const fileRef = useRef<HTMLInputElement>(null);
  /*
   * `primaryColor` and `defaultCategory` are EDITED here but are not on the
   * /auth/me membership payload, so these two seeds are placeholders, not the
   * stored values. `save()` used to send them unconditionally, which meant
   * picking a brand colour on Branding and then merely renaming the workspace
   * silently reverted the colour to this hardcoded gold. The save below now
   * sends only the fields the user actually changed.
   */
  const initial = {
    name: activeWorkspace?.workspaceName ?? "",
    slug: activeWorkspace?.workspaceSlug ?? "",
    logo: (activeWorkspace?.workspaceLogo ?? "") as string,
    primaryColor: "#C9A24A",
    defaultCategory: "general",
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const pick = async (file?: File) => {
    if (!file) return;
    try { const logo = await readImageAsDataUrl(file, 256); setForm((f) => ({ ...f, logo })); } catch { /* ignore */ }
  };
  const save = async () => {
    if (!activeWorkspace) return;
    setSaving(true); setErr(null);
    try {
      // Only the changed keys: an untouched placeholder must never be written
      // over a real stored value.
      const changed: Record<string, string> = {};
      for (const k of ["name", "slug", "logo", "primaryColor", "defaultCategory"] as const) {
        if (form[k] !== initial[k]) changed[k] = form[k];
      }
      await updateWorkspace(activeWorkspace.workspaceId, changed);
      await refreshWorkspaces();
    } catch (e) { setErr(e instanceof Error ? e.message : "Failed to save"); } finally { setSaving(false); }
  };

  if (!canManage) {
    return <Section title="Workspace" desc="Only workspace admins can edit these settings."><p className="text-[13px] text-ink-500">Contact your workspace owner to change the workspace name, logo, or color.</p></Section>;
  }

  return (
    <Section title="Workspace" desc="Branding for this workspace.">
      {err && <p className="mb-4 rounded-lg bg-rose/10 px-3 py-2 text-[12.5px] text-rose">{err}</p>}
      <div className="flex items-center gap-4">
        <button type="button" onClick={() => fileRef.current?.click()} className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-ink-200 bg-canvas text-ink-400 hover:border-gold-300">
          {form.logo ? <img src={form.logo} alt="" className="h-full w-full object-cover" /> : <ImagePlus size={20} />}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        <div>
          <p className="text-[13px] font-medium text-ink-800">Workspace logo</p>
          {form.logo && <button onClick={() => setForm((f) => ({ ...f, logo: "" }))} className="mt-1 text-[11.5px] text-rose hover:underline">Remove</button>}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Workspace name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <Field label="Slug" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") }))} />
        <Select label="Default category" value={form.defaultCategory} onChange={(e) => setForm((f) => ({ ...f, defaultCategory: e.target.value }))} options={CATEGORIES.map((c) => ({ value: c, label: c[0].toUpperCase() + c.slice(1) }))} />
        <div>
          <label className="mb-1.5 block text-[11px] font-medium text-ink-500">Brand color</label>
          <div className="flex items-center gap-2">
            {["#C9A24A", "#0F172A", "#0E7490", "#9333EA", "#DC2626", "#059669"].map((c) => (
              <button key={c} type="button" onClick={() => setForm((f) => ({ ...f, primaryColor: c }))} className={`h-7 w-7 rounded-full border-2 ${form.primaryColor === c ? "scale-110 border-ink-900" : "border-transparent"}`} style={{ background: c }} />
            ))}
          </div>
        </div>
      </div>

      <StickySaveBar visible={dirty} saving={saving} onSave={save} onDiscard={() => setForm(initial)} />
    </Section>
  );
}

// ─────────────────────────── Security ───────────────────────────────────────
function SecuritySection() {
  const [cur, setCur] = useState(""); const [next, setNext] = useState(""); const [confirm, setConfirm] = useState("");
  const [pwSaving, setPwSaving] = useState(false); const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [setupData, setSetupData] = useState<TwoFactorSetup | null>(null);
  const [code, setCode] = useState(""); const [twoFAErr, setTwoFAErr] = useState<string | null>(null); const [twoFABusy, setTwoFABusy] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  useEffect(() => { get2FAStatus().then((s) => setEnabled(s.enabled)).catch(() => setEnabled(false)); }, []);

  const submitPassword = async () => {
    setPwMsg(null);
    if (next.length < 8) { setPwMsg({ ok: false, text: "New password must be at least 8 characters" }); return; }
    if (next !== confirm) { setPwMsg({ ok: false, text: "Passwords do not match" }); return; }
    setPwSaving(true);
    try {
      await changePassword(cur, next);
      setPwMsg({ ok: true, text: "Password changed" }); setCur(""); setNext(""); setConfirm("");
    } catch (e) { setPwMsg({ ok: false, text: e instanceof Error ? e.message : "Failed" }); } finally { setPwSaving(false); }
  };

  const startSetup = async () => { setTwoFAErr(null); try { setSetupData(await setup2FA()); } catch (e) { setTwoFAErr(e instanceof Error ? e.message : "Failed"); } };
  const confirmEnable = async () => {
    setTwoFABusy(true); setTwoFAErr(null);
    try { await enable2FA(code.trim()); setEnabled(true); setSetupData(null); setCode(""); }
    catch (e) { setTwoFAErr(e instanceof Error ? e.message : "Invalid code"); } finally { setTwoFABusy(false); }
  };
  const confirmDisable = async () => {
    setTwoFABusy(true); setTwoFAErr(null);
    try { await disable2FA(code.trim()); setEnabled(false); setDisableOpen(false); setCode(""); }
    catch (e) { setTwoFAErr(e instanceof Error ? e.message : "Invalid code"); } finally { setTwoFABusy(false); }
  };

  return (
    <div className="space-y-5">
      <Section title="Change password" desc="Use a strong, unique password.">
        {pwMsg && <p className={`mb-4 rounded-lg px-3 py-2 text-[12.5px] ${pwMsg.ok ? "bg-emerald/10 text-emerald" : "bg-rose/10 text-rose"}`}>{pwMsg.text}</p>}
        <div className="grid grid-cols-1 gap-4 sm:max-w-md">
          <Field label="Current password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} />
          <Field label="New password" type="password" value={next} onChange={(e) => setNext(e.target.value)} />
          <Field label="Confirm new password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <Button variant="primary" size="sm" className="w-fit" disabled={pwSaving || !cur || !next} onClick={submitPassword}>
            {pwSaving ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Update password
          </Button>
        </div>
      </Section>

      <Section title="Two-factor authentication" desc="Add an extra layer of security with an authenticator app.">
        {enabled === null ? (
          <Loader2 className="animate-spin text-ink-400" />
        ) : enabled ? (
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald/10 px-2.5 py-1 text-[12px] font-medium text-emerald"><Check size={13} /> Enabled</span>
            <Button variant="danger" size="sm" onClick={() => { setDisableOpen(true); setCode(""); setTwoFAErr(null); }}>Disable 2FA</Button>
          </div>
        ) : setupData ? (
          <div className="flex flex-col items-start gap-4 sm:flex-row">
            <img src={setupData.qrDataUrl} alt="2FA QR" className="h-40 w-40 rounded-lg border border-ink-100" />
            <div className="flex-1">
              <p className="text-[12.5px] text-ink-600">Scan with Google Authenticator / 1Password, then enter the 6-digit code.</p>
              <p className="mt-2 break-all rounded bg-ink-50 px-2 py-1 font-mono text-[11px] text-ink-500">{setupData.secret}</p>
              <details className="mt-2 text-[11.5px] text-ink-500">
                <summary className="cursor-pointer">Recovery codes (save these)</summary>
                <div className="mt-1 grid grid-cols-2 gap-1 font-mono">{setupData.recoveryCodes.map((c) => <span key={c}>{c}</span>)}</div>
              </details>
              {twoFAErr && <p className="mt-2 text-[12px] text-rose">{twoFAErr}</p>}
              <div className="mt-3 flex items-center gap-2">
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" className="w-28 rounded-lg border border-ink-200 px-3 py-2 text-center font-mono text-[14px] focus:border-gold-400 focus:outline-none" />
                <Button variant="primary" size="sm" disabled={twoFABusy || code.length < 6} onClick={confirmEnable}>{twoFABusy ? <Loader2 size={14} className="animate-spin" /> : null} Verify & enable</Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-ink-500">Not enabled</span>
            <Button variant="secondary" size="sm" onClick={startSetup}>Enable 2FA</Button>
          </div>
        )}
      </Section>

      <Modal open={disableOpen} onClose={() => setDisableOpen(false)} title="Disable two-factor" size="sm"
        footer={<><Button variant="ghost" size="sm" onClick={() => setDisableOpen(false)}>Cancel</Button><Button variant="danger" size="sm" disabled={twoFABusy || code.length < 6} onClick={confirmDisable}>{twoFABusy ? <Loader2 size={13} className="animate-spin" /> : null} Disable</Button></>}>
        <p className="text-[13px] text-ink-600">Enter a current 6-digit code to turn off two-factor authentication.</p>
        {twoFAErr && <p className="mt-2 text-[12px] text-rose">{twoFAErr}</p>}
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" className="mt-3 w-32 rounded-lg border border-ink-200 px-3 py-2 text-center font-mono text-[14px] focus:border-gold-400 focus:outline-none" />
      </Modal>
    </div>
  );
}

// ─────────────────────────── Danger zone ────────────────────────────────────
function DangerSection() {
  const { activeWorkspace, refreshWorkspaces } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const del = async () => {
    if (!activeWorkspace) return;
    setBusy(true); setErr(null);
    try {
      await deleteWorkspace(activeWorkspace.workspaceId);
      localStorage.removeItem("active_workspace_id");
      await refreshWorkspaces();
      window.location.assign("/select-workspace");
    } catch (e) { setErr(e instanceof Error ? e.message : "Failed to delete"); setBusy(false); }
  };

  return (
    <Section title="Danger zone" desc="Irreversible actions.">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose/30 bg-rose/5 px-4 py-3">
        <div>
          <p className="text-[13px] font-semibold text-ink-900">Delete this workspace</p>
          <p className="text-[12px] text-ink-500">Permanently deletes <span className="font-medium">{activeWorkspace?.workspaceName}</span> and all its models, embeds, and domains.</p>
        </div>
        <Button variant="danger" size="sm" onClick={() => { setOpen(true); setConfirmText(""); setErr(null); }}>Delete workspace</Button>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Delete workspace" size="sm"
        footer={<><Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button><Button variant="danger" size="sm" disabled={busy || confirmText !== activeWorkspace?.workspaceName} onClick={del}>{busy ? <Loader2 size={13} className="animate-spin" /> : null} Delete forever</Button></>}>
        <p className="text-[13px] text-ink-600">Type <span className="font-semibold">{activeWorkspace?.workspaceName}</span> to confirm. This cannot be undone.</p>
        {err && <p className="mt-2 text-[12px] text-rose">{err}</p>}
        <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="mt-3 w-full rounded-lg border border-ink-200 px-3 py-2 text-[13px] focus:border-rose focus:outline-none" />
      </Modal>
    </Section>
  );
}
